<template>
  <main v-if="user" class="flex justify-center min-h-screen bg-gray-100">
    <HeaderComponent />
    <section class="w-full pb-[100px] lg:pl-[50px] lg:pt-[95px] pt-[95px] max-h-screen overflow-y-auto relative flex justify-start flex-col lg:p-[25px] p-[20px] h-auto min-h-screen bg-gray-100">
      <!-- Notifications -->
      <div class="fixed top-20 left-1/2 lg:left-[calc(50%+140px)] z-50 transform -translate-x-1/2 w-full max-w-md px-4">
        <transition name="fade">
          <div v-if="errorMessage || successMessage">
            <div v-if="errorMessage" class="bg-red-500 text-white p-4 rounded-lg shadow-lg mb-2">
              {{ errorMessage }}
            </div>
            <div v-if="successMessage" class="bg-green-500 text-white p-4 rounded-lg shadow-lg mb-2">
              {{ successMessage }}
            </div>
          </div>
        </transition>

      </div>

      <!-- Profile Header -->
      <div class="relative mb-8">
        <div class="absolute inset-x-0 top-0 h-48 bg-orange-500 rounded-lg"></div>
        <div class="relative z-10 px-6 pt-6">
          <div class="max-w-6xl mx-auto">
            <div class="flex flex-col md:flex-row gap-6">
              <!-- Profile Card -->
              <div class="w-full md:w-1/3 bg-white rounded-xl shadow-md overflow-hidden">
                <div class="p-6 flex flex-col items-center">
                  <div class="relative">
                    <img src="../assets/images/icone.png" alt="Profile" class="w-32 h-32 rounded-full object-cover border-4 border-white shadow">
                    <button class="absolute bottom-0 right-0 w-10 min-w-[40px] h-10 hidden items-center justify-center bg-orange-500 text-white p-2 rounded-full hover:bg-orange-600 transition">
                      <i class="fas fa-camera text-sm"></i>
                    </button>
                  </div>
                  <h1 class="mt-4 text-xl font-bold text-gray-800">{{ user.firstname }} {{ user.lastname }}</h1>
                  <p class="text-gray-600">{{ user.email }}</p>
                  
                  <div class="w-full mt-6 space-y-3">
                    <div class="flex justify-between items-center">
                      <span class="text-sm font-medium text-gray-500">Poste</span>
                      <span class="text-sm font-semibold">{{ user.poste }}</span>
                    </div>
                    <div class="flex justify-between items-center">
                      <span class="text-sm font-medium text-gray-500">Identifiant</span>
                      <span class="text-sm font-semibold">{{ user.username }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Profile Content -->
              <div class="w-full md:w-2/3 bg-white rounded-xl shadow-md overflow-hidden">
                <!-- Tabs -->
                <div class="border-b border-gray-200">
                  <nav class="flex -mb-px">
                    <button 
                      @click="toggleInfo()"
                      :class="information ? 'text-orange-500 border-orange-500' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                      class="whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm"
                    >
                      Informations
                    </button>
                    <button 
                      @click="togglePassword()"
                      :class="password ? 'text-orange-500 border-orange-500' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'"
                      class="whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm"
                    >
                      Mot de passe
                    </button>
                  </nav>
                </div>

                <!-- Information Tab -->
                <div v-if="information" class="p-6">
                  <form @submit.prevent="updateUser" class="space-y-4">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Prénoms <span class="text-red-500">*</span></label>
                        <input 
                          type="text" 
                          v-model="user.firstname"
                          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                        >
                      </div>
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Nom <span class="text-red-500">*</span></label>
                        <input 
                          type="text" 
                          v-model="user.lastname"
                          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                        >
                      </div>
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Email <span class="text-red-500">*</span></label>
                      <input 
                        type="email" 
                        v-model="user.email"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      >
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Numéro <span class="text-red-500">*</span></label>
                      <input 
                        type="text" 
                        v-model="user.phone"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      >
                    </div>
                    <div class="pt-2">
                      <button type="submit" class="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 rounded-lg font-medium transition">
                        Modifier les informations
                      </button>
                    </div>
                  </form>
                </div>

                <!-- Password Tab -->
                <div v-if="password" class="p-6">
                  <form @submit.prevent="changePassword" class="space-y-4">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Ancien mot de passe <span class="text-red-500">*</span></label>
                      <input 
                        type="password" 
                        v-model="currentPassword"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      >
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe <span class="text-red-500">*</span></label>
                      <input 
                        type="password" 
                        v-model="newPassword"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      >
                    </div>
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Confirmer le nouveau mot de passe <span class="text-red-500">*</span></label>
                      <input 
                        type="password" 
                        v-model="confirmPassword"
                        class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-orange-500 focus:border-orange-500"
                      >
                    </div>
                    <div class="pt-2">
                      <button type="submit" class="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 rounded-lg font-medium transition">
                        Modifier le mot de passe
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import HeaderComponent from "../components/HeaderComponent.vue"
import { ref, onMounted } from 'vue'
import axios from 'axios'
import { useAuthStore } from '@/store/auth';
import { computed } from 'vue';

const authStore = useAuthStore();
const user = computed(() => authStore.user);

const errorMessage = ref('')
const successMessage = ref('')
const information = ref(true)
const password = ref(false)
const currentPassword = ref('')
const newPassword = ref('')
const confirmPassword = ref('')

const hideSuccessMessageAfterDelay = () => {
  setTimeout(() => {
    successMessage.value = ''; 
  }, 5000);
};

const hideErrorMessageAfterDelay = () => {
  setTimeout(() => {
    errorMessage.value = ''; 
  }, 10000);
};

function toggleInfo() {
  information.value = true 
  password.value = false
}

function togglePassword() {
  password.value = true 
  information.value = false
}

const updateUser = async () => {
  try {
    await axios.put(`/api/users/${user.value._id}`, user.value)
    localStorage.setItem('user', JSON.stringify(user.value));
    successMessage.value = 'Informations mises à jour avec succès'
    hideSuccessMessageAfterDelay()
  } catch (error) {
    errorMessage.value = 'Erreur lors de la mise à jour des informations'
    hideErrorMessageAfterDelay()
  }
}

const changePassword = async () => {
  if (newPassword.value !== confirmPassword.value) {
    errorMessage.value = 'Les nouveaux mots de passe ne correspondent pas'
    hideErrorMessageAfterDelay()
    return
  }
  try {
    await axios.post('/api/reset-password', {
      email: user.value.email,
      oldPassword: currentPassword.value,
      newPassword: newPassword.value
    })
    successMessage.value = 'Mot de passe mis à jour avec succès'
    hideSuccessMessageAfterDelay()
    currentPassword.value = ''
    newPassword.value = ''
    confirmPassword.value = ''
  } catch (error) {
    errorMessage.value = 'Erreur lors de la mise à jour du mot de passe'
    hideErrorMessageAfterDelay()
  }
}

onMounted(() => {
  const userData = localStorage.getItem('user');
  if (userData) {
    user.value = JSON.parse(userData);
  }
});
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Animation for button hover */
.hover-scale {
  transition: transform 0.2s ease;
}
.hover-scale:hover {
  transform: translateY(-2px);
}
</style>